import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {
  analysisInputGridRowSx,
  analysisInputListItemDividerSx,
} from './workforceProfileAnalysisDataInputSectionSx';
import {WorkforceProfileAnalysisInputCandidateProfileFields} from './WorkforceProfileAnalysisInputCandidateProfileFields';
import {WorkforceProfileAnalysisInputExtractedSkillsList} from './WorkforceProfileAnalysisInputExtractedSkillsList';
import {WorkforceProfileAnalysisInputSubsectionTitle} from './WorkforceProfileAnalysisInputSubsectionTitle';
import {InfoBanner} from '../../../../../components/InfoBanner/InfoBanner';
import {WorkforceProfileAnalysisDataInput} from '../../types/WorkforceProfileTypes';

export interface Props {
  data: WorkforceProfileAnalysisDataInput;
}

export const WorkforceProfileAnalysisDataInputSectionContent = ({data}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.analysisInput'});

  const confidenceLabel = (value: string) => t(`confidence.${value.toLowerCase()}`, {defaultValue: value});

  return (
    <Box>
      <InfoBanner text={t('description')} sx={{mb: 2.5}} />

      {data.candidateProfile && (
        <Box sx={{mb: 2.5}}>
          <WorkforceProfileAnalysisInputSubsectionTitle title={t('candidateProfile')} />
          <WorkforceProfileAnalysisInputCandidateProfileFields
            profile={data.candidateProfile}
            labels={{
              summary: t('fields.summary'),
              education: t('fields.education'),
              responsibilities: t('fields.responsibilities'),
              senioritySignals: t('fields.senioritySignals'),
              industryExperience: t('fields.industryExperience'),
              workingStyleNotes: t('fields.workingStyleNotes'),
            }}
          />
        </Box>
      )}

      {data.skillsCompetencies.length > 0 && (
        <Box sx={{mb: 2.5}}>
          <WorkforceProfileAnalysisInputSubsectionTitle
            title={t('skillsCompetencies')}
            count={data.skillsCompetencies.length}
          />
          <WorkforceProfileAnalysisInputExtractedSkillsList
            skills={data.skillsCompetencies}
            confidenceLabel={confidenceLabel}
          />
        </Box>
      )}

      {data.knowledgeSkills.length > 0 && (
        <Box sx={{mb: 2.5}}>
          <WorkforceProfileAnalysisInputSubsectionTitle
            title={t('knowledgeSkills')}
            count={data.knowledgeSkills.length}
          />
          <WorkforceProfileAnalysisInputExtractedSkillsList
            skills={data.knowledgeSkills}
            confidenceLabel={confidenceLabel}
          />
        </Box>
      )}

      {data.workExperiences.length > 0 && (
        <Box sx={{mb: 2.5}}>
          <WorkforceProfileAnalysisInputSubsectionTitle
            title={t('workExperiences')}
            count={data.workExperiences.length}
          />
          {data.workExperiences.map((experience, index) => (
            <Box
              key={`${experience.roleTitle}-${experience.duration}-${index}`}
              sx={analysisInputListItemDividerSx}
            >
              <Typography variant='body2' sx={{fontWeight: 600}}>
                {experience.roleTitle}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 0.25}}>
                {experience.organizationDescriptor}
                {experience.duration ? ` · ${experience.duration}` : ''}
              </Typography>
              {experience.highlights.length > 0 && (
                <Box component='ul' sx={{m: 0, mt: 1, pl: 2.5}}>
                  {experience.highlights.map((highlight, hi) => (
                    <Typography
                      component='li'
                      key={`${hi}-${highlight}`}
                      variant='caption'
                      color='text.secondary'
                      sx={{lineHeight: 1.5, mb: 0.5}}
                    >
                      {highlight}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {data.languages.length > 0 && (
        <Box sx={{mb: 2.5}}>
          <WorkforceProfileAnalysisInputSubsectionTitle title={t('languages')} count={data.languages.length} />
          <Grid container spacing={0} columnSpacing={3}>
            {data.languages.map((language) => (
              <Grid item xs={12} md={6} key={`${language.language}-${language.level}`}>
                <Box sx={analysisInputGridRowSx}>
                  <Typography variant='body2'>
                    {language.language}
                    {language.level ? ` · ${language.level}` : ''}
                  </Typography>
                  {language.evidence && (
                    <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 0.5}}>
                      {language.evidence}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {data.certifications.length > 0 && (
        <Box sx={{mb: 2.5}}>
          <WorkforceProfileAnalysisInputSubsectionTitle
            title={t('certifications')}
            count={data.certifications.length}
          />
          <Box component='ul' sx={{m: 0, pl: 2.5}}>
            {data.certifications.map((certification) => (
              <Typography
                component='li'
                key={certification}
                variant='body2'
                color='text.secondary'
                sx={{lineHeight: 1.6, mb: 0.5}}
              >
                {certification}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {data.gapsOrAmbiguities.length > 0 && (
        <Box>
          <WorkforceProfileAnalysisInputSubsectionTitle
            title={t('gapsOrAmbiguities')}
            count={data.gapsOrAmbiguities.length}
          />
          <Box component='ul' sx={{m: 0, pl: 2.5}}>
            {data.gapsOrAmbiguities.map((gap) => (
              <Typography
                component='li'
                key={gap}
                variant='body2'
                color='text.secondary'
                sx={{lineHeight: 1.6, mb: 0.5}}
              >
                {gap}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
